import React, { useCallback, useEffect, useRef, useState } from 'react';
import SummaryTile from './SummaryTile';
import { tilesPerRow } from './renderers/SummaryRowRenderer';

// The live Summary row: a strip of independent tiles, each running its own query.
//
// This component's whole job is to let the tiles be independent while still writing
// ONE result back. `Insights.handleWidgetRefreshed` PATCHes the widget's entire
// `data` blob, so a tile reporting its own result directly would overwrite whatever
// its siblings had just written. Instead every tile reports here, and the row
// persists the assembled strip once, after the last tile settles.
//
// Tiles keep their position whatever happens to them. A failed tile renders in place
// saying so, rather than dropping out — a strip that renumbers itself on a failure
// changes what every tile after it appears to mean.

function SummaryRow({ widget, refreshKey, onRefreshed, width }) {
  const tiles = Array.isArray(widget?.tiles) ? widget.tiles : [];
  const [results, setResults] = useState({});
  // Reported once per refresh pass. Without this the effect below re-fires on every
  // parent render once the strip is complete, and each one is a PATCH.
  const reportedFor = useRef(null);

  const handleSettled = useCallback((index, result) => {
    setResults((prev) => (prev[index] === result ? prev : { ...prev, [index]: result }));
  }, []);

  // A new refresh pass invalidates the last one's results and its write.
  useEffect(() => {
    setResults({});
    reportedFor.current = null;
  }, [refreshKey]);

  const complete = tiles.length > 0 && tiles.every((_, i) => results[i] !== undefined);
  useEffect(() => {
    if (!complete) return;
    const key = `${refreshKey}|${tiles.length}`;
    if (reportedFor.current === key) return;
    reportedFor.current = key;
    const assembled = tiles.map((_, i) => results[i]);
    // Only write when something actually resolved. A strip where every tile failed
    // is not a result worth persisting over the last good one.
    if (assembled.some((t) => t && !t.error_type)) {
      onRefreshed?.({ shape: 'summary-row', tiles: assembled });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complete, refreshKey]);

  if (!tiles.length) {
    return <div className="wc-sr-empty">This summary row has no tiles yet.</div>;
  }

  const perRow = tilesPerRow(width, tiles.length);
  return (
    <div
      className="wc-sr-strip"
      role="group"
      aria-label={widget?.title ? `${widget.title}, ${tiles.length} tiles` : `${tiles.length} tiles`}
      style={{ gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))` }}
    >
      {tiles.map((tile, i) => (
        <SummaryTile
          // The key carries everything that decides what this tile ASKS, dataset
          // included, so a remount is the backstop if the effect's deps ever drift.
          key={`${i}|${tile?.kind}|${tile?.metric || tile?.prompt || ''}|${(tile?.dataset_ids || []).join(',')}`}
          tile={tile}
          index={i}
          widgetId={widget?.id}
          onSettled={handleSettled}
          refreshKey={refreshKey}
        />
      ))}
    </div>
  );
}

export default SummaryRow;
